import { isNativeCapacitorRuntime } from './revenueCatService';
import { persistAttribution, extractAttributionParams } from '../utils/attribution';

// First-party mobile App Store / deep-link acquisition attribution (item (a)).
//
// The existing attribution stack (utils/attribution.js) is driven entirely by
// window.location.search, so an App Store install — whose launch URL carries no
// UTM/gclid — lands 100% unattributed. This bridges that gap on the native
// runtime: when the app is opened via a deep link / Universal Link that DOES
// carry attribution params (e.g. a marketing https link), we feed those params
// into the SAME first-party attribution store the web flow uses, so the
// existing checkout-time backend attribution picks them up unchanged.
//
// Deliberately built on attribution.js's PUBLIC API (persistAttribution) with
// zero changes to that module (owned by another lane). First-party only (no
// IDFA / ad SDK); the AdServices token path + Universal-Links routing (AASA /
// Associated Domains) are separate, Apple-gated pieces tracked in the tranche
// scope doc. Native-only and never-throws.

// Guard so we don't double-bind the appUrlOpen listener across re-mounts.
let acquisitionListenerBound = false;

// Persist attribution from a single deep-link URL. Builds a location-like
// object from the URL and hands it to the shared persistAttribution, which
// extracts the query params and merges them (first-touch preserved). Passes an
// empty referrer so the native WebView's capacitor://localhost referrer is not
// mistaken for an external acquisition source. Never throws; returns the params
// captured (or null).
export const captureDeepLinkAttribution = (url) => {
  if (!url) {
    return null;
  }

  try {
    const location = new URL(url);
    // Only persist (and report) when THIS link actually carries an attribution
    // signal — otherwise persistAttribution no-ops and returning the stored
    // latestTouch would misreport a prior link's params as this one's.
    const params = extractAttributionParams(location.search);
    if (Object.keys(params).length === 0) {
      return null;
    }

    persistAttribution({
      location,
      referrer: '',
      storage: typeof window !== 'undefined' ? window.localStorage : undefined
    });
    return params;
  } catch (parseError) {
    return null;
  }
};

// Entry point (call once at app bootstrap): capture attribution from the launch
// URL and from subsequent deep links. No-op off the native runtime; fails soft
// if @capacitor/app is unavailable. Never throws.
export const initMobileAcquisitionAttribution = async () => {
  if (!isNativeCapacitorRuntime()) {
    return { started: false, reason: 'not-native' };
  }

  let App;
  try {
    ({ App } = await import('@capacitor/app'));
  } catch (importError) {
    return { started: false, reason: 'plugin-unavailable' };
  }

  if (!App) {
    return { started: false, reason: 'plugin-unavailable' };
  }

  try {
    const launch = await App.getLaunchUrl();
    if (launch?.url) {
      captureDeepLinkAttribution(launch.url);
    }

    if (!acquisitionListenerBound) {
      App.addListener('appUrlOpen', (event) => {
        captureDeepLinkAttribution(event?.url);
      });
      // Set only after a successful bind, so a throwing addListener doesn't
      // permanently latch the guard and lose the listener on later retries.
      acquisitionListenerBound = true;
    }

    return { started: true };
  } catch (error) {
    return { started: false, reason: 'init-exception' };
  }
};

// Test seam: reset the one-time listener guard.
export const resetAcquisitionListenerBindingForTests = () => {
  acquisitionListenerBound = false;
};
